import { isEmpty, isNil } from 'lodash'
import type { Receivable, ReceivableGateway } from '@/shared/models/receivableModels'
import { differenceInDays } from 'date-fns'
import { validateSameCurrency, validateSameDebtorId } from './tableUtils'

export const receivableInvoiceIsDueSoon = (receivable: ReceivableGateway): boolean => {
    const { expectedInvoiceDueDate } = receivable
    if (expectedInvoiceDueDate && isNil(receivable.invoiceId)) {
        const diffInDays = differenceInDays(new Date(expectedInvoiceDueDate), new Date(Date.now()))
        return diffInDays <= 7 && diffInDays >= 0
    }
    return false
}

export const receivableInvoiceIsOverdue = (receivable: ReceivableGateway): boolean => {
    const { expectedInvoiceDueDate } = receivable
    if (expectedInvoiceDueDate && isNil(receivable.invoiceId)) {
        const dueDate = new Date(expectedInvoiceDueDate)
        const dueDateDifferenceInDays = differenceInDays(new Date(Date.now()), new Date(dueDate))
        return dueDateDifferenceInDays > 0
    }
    return false
}

export const expectedReceivableDueDateDifferenceInDays = (receivable: Receivable) => {
    if (receivable?.expectedInvoiceDueDate) {
        return Math.abs(differenceInDays(new Date(receivable.expectedInvoiceDueDate), new Date(Date.now())))
    }
}

type ActionPermission = { enabled: boolean; tooltip?: string }

export const getReceivableActionPermissions = (
    selectedReceivables: (Receivable | undefined)[],
    tooltipMessages: {
        noReceivableWarningTooltip: string
        differentCurrenciesWarningTooltip: string
        differentDebtorsWarningTooltip: string
        receivableHasInvoiceWarningTooltip: string
        receivableHasNoInvoiceWarningTooltip: string
    }
): {
    canLink: ActionPermission
    canUnlink: ActionPermission
} => {
    const validReceivables = selectedReceivables.filter((receivable) => !isNil(receivable))
    const invalidReceivables = selectedReceivables.filter((receivable) => isNil(receivable))

    if (isEmpty(selectedReceivables) || isEmpty(validReceivables) || !isEmpty(invalidReceivables)) {
        return {
            canLink: {
                enabled: false,
                tooltip: tooltipMessages.noReceivableWarningTooltip
            },
            canUnlink: {
                enabled: false,
                tooltip: tooltipMessages.noReceivableWarningTooltip
            }
        }
    }

    const allSelectedReceivablesHaveSameCurrency = validateSameCurrency(
        validReceivables,
        tooltipMessages.differentCurrenciesWarningTooltip
    )

    const allReceivablesHaveSameDebtor = validateSameDebtorId(
        validReceivables,
        tooltipMessages.differentDebtorsWarningTooltip
    )

    const someSelectedReceivablesHaveAnInvoice = validReceivables.some((receivable) => !isNil(receivable.invoiceId))
    const allSelectedReceivablesHaveAnInvoice = validReceivables.every((receivable) => !isNil(receivable.invoiceId))

    const canLink = () => {
        if (!allSelectedReceivablesHaveSameCurrency.isValid) {
            return {
                enabled: false,
                tooltip: allSelectedReceivablesHaveSameCurrency.reason
            }
        }

        if (!allReceivablesHaveSameDebtor.isValid) {
            return {
                enabled: false,
                tooltip: allReceivablesHaveSameDebtor.reason
            }
        }

        if (someSelectedReceivablesHaveAnInvoice) {
            return {
                enabled: false,
                tooltip: tooltipMessages.receivableHasInvoiceWarningTooltip
            }
        }

        return { enabled: true }
    }

    const canUnlink = () => {
        if (!allSelectedReceivablesHaveAnInvoice) {
            return {
                enabled: false,
                tooltip: tooltipMessages.receivableHasNoInvoiceWarningTooltip
            }
        }

        return { enabled: true }
    }

    return { canLink: canLink(), canUnlink: canUnlink() }
}
